// features/workstations/dashboard-sections/pattern-library-status.js
// ═══════════════════════════════════════════════════════════
// Pattern Library Status (Day Trading dashboard)
// ═══════════════════════════════════════════════════════════
// Pulls trade analytics for this workstation and shows
// per-setup edge metrics. The "pattern library" = the user's
// configured setups; "status" = whether each one is working
// right now based on trade history.
//
// Goes live when the user has any closed trades. Until then
// it shows a friendly empty state pointing to /trade.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import ShiftRead from './shift-read';

export default function PatternLibraryStatusSection({ workstation }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workstation?.id) return;
    let cancelled = false;
    (async () => {
      try {
        // user_id comes from workstation.user_id (or null for system seed).
        // For seeds the analytics will be empty until the user logs trades.
        const userId = workstation.user_id || '';
        if (!userId) {
          setLoading(false);
          return;
        }
        const res = await fetch(
          `/api/workstations/trade-analytics?workstation_id=${workstation.id}&user_id=${encodeURIComponent(userId)}&days=30`,
          { cache: 'no-store' },
        );
        if (cancelled) return;
        const data = res.ok ? await res.json() : null;
        setAnalytics(data);
      } catch (e) {
        console.warn('[pattern-library] load failed:', e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workstation?.id, workstation?.user_id]);

  const setups = analytics?.by_setup || [];
  const overall = analytics?.overall;
  const hasData = overall && overall.total > 0;

  return (
    <section className="ws-section ws-section-pattern-library">
      <div className="ws-section-header">
        <div className="ws-section-label">Pattern Library Status</div>
        <div className="ws-section-tag">Last 30 days</div>
      </div>
      <div className="ws-section-body">
        {loading && <div className="ws-loading">Loading…</div>}
        {!loading && !hasData && (
          <div className="ws-pattern-empty">
            <div className="ws-pattern-empty-title">No closed trades yet</div>
            <div className="ws-pattern-empty-body">
              In Think, log a trade with <code>/trade ...</code> and close it with{' '}
              <code>/close ...</code>. Per-setup win rate, expectancy, and edge signals
              appear here automatically as your history builds.
            </div>
          </div>
        )}
        {!loading && hasData && (
          <>
            <div className="ws-pattern-overview">
              <Stat label="Total" value={overall.total} />
              <Stat label="Win" value={overall.win_rate != null ? `${Math.round(overall.win_rate * 100)}%` : '—'} />
              <Stat
                label="Expectancy"
                value={overall.expectancy_r != null ? `${overall.expectancy_r > 0 ? '+' : ''}${overall.expectancy_r.toFixed(2)}R` : '—'}
                tone={overall.expectancy_r > 0 ? 'sage' : overall.expectancy_r < 0 ? 'terra' : 'mid'}
              />
              <Stat
                label="P&L"
                value={`$${overall.total_pnl.toFixed(2)}`}
                tone={overall.total_pnl > 0 ? 'sage' : overall.total_pnl < 0 ? 'terra' : 'mid'}
              />
              <Stat
                label="Streak"
                value={overall.current_streak === 0 ? '—' : `${overall.current_streak > 0 ? 'W' : 'L'}×${Math.abs(overall.current_streak)}`}
                tone={overall.current_streak > 0 ? 'sage' : overall.current_streak < 0 ? 'terra' : 'mid'}
              />
            </div>
            <div className="ws-pattern-bysetup">
              {setups.map((s) => {
                const wr = s.win_rate != null ? Math.round(s.win_rate * 100) : null;
                const tone = s.avg_r > 0 ? 'sage' : s.avg_r < 0 ? 'terra' : 'mid';
                const signal = signalFor(s);
                return (
                  <div key={s.setup_type} className="ws-pattern-row">
                    <div className="ws-pattern-row-name">
                      {s.setup_type}
                      {signal && <span className={`ws-pattern-row-signal ws-pattern-row-signal-${signal.tone}`}>{signal.label}</span>}
                    </div>
                    <div className="ws-pattern-row-stats">
                      <span>{s.total} trades</span>
                      <span>· {wr != null ? `${wr}% WR` : 'no WR'}</span>
                      <span className={`ws-pattern-row-r ${tone}`}>
                        {s.avg_r != null ? `${s.avg_r > 0 ? '+' : ''}${s.avg_r.toFixed(2)}R` : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <ShiftRead>
              {bestEdgeLine(setups)}
            </ShiftRead>
          </>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="ws-stat">
      <div className="ws-stat-label">{label}</div>
      <div className={`ws-stat-value${tone ? ' ' + tone : ''}`}>{value}</div>
    </div>
  );
}

function signalFor(s) {
  if (s.total < 3) return null;
  if (s.win_rate != null && s.win_rate < 0.35) return { label: 'cold', tone: 'terra' };
  if (s.avg_r != null && s.avg_r < -0.3) return { label: 'bleeding', tone: 'terra' };
  if (s.win_rate != null && s.win_rate > 0.7) return { label: 'hot', tone: 'sage' };
  return null;
}

function bestEdgeLine(setups) {
  if (!setups || setups.length === 0) return 'Add a few more closed trades and the per-setup edge picture sharpens.';
  const ranked = [...setups].filter((s) => s.total >= 3 && s.avg_r != null).sort((a, b) => b.avg_r - a.avg_r);
  if (ranked.length === 0) return 'Need more closed trades per setup before signals firm up — keep logging.';
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];
  if (best.setup_type === worst.setup_type) {
    return `Only ${best.setup_type} has enough sample. ${best.avg_r > 0 ? 'Edge holding.' : 'Edge negative — review the entries.'}`;
  }
  return `${best.setup_type} carrying the book (${best.avg_r > 0 ? '+' : ''}${best.avg_r.toFixed(2)}R avg). ${worst.setup_type} is the drag (${worst.avg_r > 0 ? '+' : ''}${worst.avg_r.toFixed(2)}R) — worth a debug session.`;
}
